/**
 * Bridges the classified click features into `resolveClick` candidates, each paired (by object
 * identity) with the concrete `ClickTarget` needed to execute it. `resolveClick` only sorts and
 * filters candidates (it never clones them) so the returned `targets` map still resolves every
 * candidate the resolver emits.
 */
import {
  FeatureWithEntityType,
  MapDrawerEntityForPoi,
  OverlappingFeatureAction,
  StandardPoi,
} from 'features/mapping/mapping.types';
import { ExternalGeoEvent, GeoEventEvacZoneStatus } from 'shared/types';
import { MapGeoJSONFeature } from 'shared/map-exports';
import {
  ClickCandidate,
  ClickGeometry,
} from 'features/mapping/mapClick/resolution';
import {
  MapClickTargetType,
  NAVIGATIONAL_TYPES,
} from 'features/mapping/mapClick/priority';
import { RouterEntityDrawer } from '../../../constants';
import {
  deduplicateFeatures,
  filterFeaturesByEntityTypes,
} from './interactiveFeatures';
import { getClickGeometry } from './featureClassification';
import {
  collectDrawerEntityPois,
  collectPois,
  resolvePoiAction,
  resolveRouterEntityActions,
  sortPoiFeatures,
} from './poiActions';
import {
  CLICK_GEOMETRY_BY_ROUTER_ENTITY,
  ENTITY_DRAWER_POI_HANDLERS,
  NAVIGATIONAL_HANDLERS,
  NavigationalClickContext,
  POI_HANDLERS,
  ROUTER_ENTITY_DRAWER_HANDLERS,
} from './constants';

export type ClickTarget =
  | {
      category: 'router';
      entityType: RouterEntityDrawer;
      action: OverlappingFeatureAction;
      navigate: () => void;
    }
  | { category: 'poi'; poi: StandardPoi; action: OverlappingFeatureAction }
  | {
      category: 'drawerEntity';
      entity: MapDrawerEntityForPoi;
      action: OverlappingFeatureAction;
    }
  | {
      // photoMarker / report short-circuits: no overlapping-feature action, just a side effect
      // (they navigate away). `run` fires their existing layer click handler.
      category: 'navigational';
      run: () => void;
    };

/**
 * Identity-keyed map from each candidate to the concrete thing to execute. Keyed by object
 * identity, so it only resolves the exact `ClickCandidate` objects built alongside it.
 */
export type ClickTargets = Map<ClickCandidate, ClickTarget>;

type BuiltClickCandidates = {
  candidates: ClickCandidate[];
  targets: ClickTargets;
};

type BuildClickCandidatesInput = {
  /** Every feature under the pointer, unfiltered, straight from `gatherClickFeatures`. */
  features: FeatureWithEntityType[];
  navigationalContext: NavigationalClickContext;
  activeEvacZones: GeoEventEvacZoneStatus[];
  externalGeoEvents: ExternalGeoEvent[];
};

export const buildClickCandidates = ({
  features,
  navigationalContext,
  activeEvacZones,
  externalGeoEvents,
}: BuildClickCandidatesInput): BuiltClickCandidates => {
  const candidates: ClickCandidate[] = [];
  const targets: ClickTargets = new Map();

  const push = (
    type: MapClickTargetType,
    geometry: ClickGeometry,
    target: ClickTarget,
  ): void => {
    const candidate: ClickCandidate = { type, geometry };
    candidates.push(candidate);
    targets.set(candidate, target);
  };

  // This is a dumb translator: it pushes a candidate for every hit and makes no decisions -
  // `resolveClick` does. Building is deliberately eager (all handlers run for every hit); it is
  // cheap for the handful of overlapping features under a click, and keeping it decision-free is
  // what lets the resolver stay the single source of ordering + interaction. See ./README.md.

  // Navigational: build order comes from NAVIGATIONAL_TYPES, not from this loop.
  for (const type of NAVIGATIONAL_TYPES) {
    const hits = filterFeaturesByEntityTypes(features, [type]);
    if (hits.length) {
      const handler = NAVIGATIONAL_HANDLERS[type];
      const navigationalFeatures = hits.map(({ feature }) => feature);
      push(type, 'point', {
        category: 'navigational',
        run: () => handler(navigationalFeatures, navigationalContext),
      });
    }
  }

  // Routers: `resolveRouterEntityActions` dedups + orders same-type by comparator (e.g. NWS alert
  // rank); the resolver's stable sort preserves that within-type order. A router navigates with all
  // features of its type, so group them and build each target's `navigate` from the whole group.
  const dedupedRouters = deduplicateFeatures(
    filterFeaturesByEntityTypes(
      features,
      Object.keys(ROUTER_ENTITY_DRAWER_HANDLERS),
    ),
  );
  const routerFeaturesByType = new Map<
    RouterEntityDrawer,
    MapGeoJSONFeature[]
  >();
  for (const { feature, entityType } of dedupedRouters) {
    const type = entityType as RouterEntityDrawer;
    const group = routerFeaturesByType.get(type) ?? [];
    group.push(feature);
    routerFeaturesByType.set(type, group);
  }

  for (const action of resolveRouterEntityActions(dedupedRouters)) {
    if (action.category === 'routerEntity') {
      const entityType = action.entityType as RouterEntityDrawer;
      const handler = ROUTER_ENTITY_DRAWER_HANDLERS[entityType];
      const routerFeatures = routerFeaturesByType.get(entityType) ?? [];
      push(entityType, CLICK_GEOMETRY_BY_ROUTER_ENTITY[entityType], {
        category: 'router',
        entityType,
        action,
        navigate: () => handler(routerFeatures),
      });
    }
  }

  // POIs + drawer entities: resolve per feature so geometry (incl. dynamic powerOutage) is exact;
  // the resolver applies mutual exclusion and classifies overlap / select / empty.
  const poiCandidates = filterFeaturesByEntityTypes(
    features,
    Object.keys(POI_HANDLERS),
  );
  for (const feature of sortPoiFeatures(deduplicateFeatures(poiCandidates))) {
    const geometry = getClickGeometry(feature);
    for (const poi of collectPois(
      [feature],
      activeEvacZones,
      externalGeoEvents,
    )) {
      push(poi.type, geometry, {
        category: 'poi',
        poi,
        action: resolvePoiAction(poi),
      });
    }
  }

  const drawerCandidates = filterFeaturesByEntityTypes(
    features,
    Object.keys(ENTITY_DRAWER_POI_HANDLERS),
  );
  for (const feature of deduplicateFeatures(drawerCandidates)) {
    const geometry = getClickGeometry(feature);
    for (const entity of collectDrawerEntityPois([feature])) {
      push(entity.type, geometry, {
        category: 'drawerEntity',
        entity,
        action: { category: 'drawerEntityPoi', entity },
      });
    }
  }

  return { candidates, targets };
};
